import { createAdminSession, readAdminError } from './admin-session';
import {
    buildChannelOptions,
    defaultSortDirection,
    formatChannelName,
    formatExposure,
    formatHourlyRateCounts,
    formatPercent,
    formatPerHour,
    formatRateCounts,
    formatReferrerHost,
    formatReferrerPath,
    formatTimeZoneName,
    formatTodayExposureStart,
    formatViewDuration,
    hasReferrerBreakdown,
    hasReferrerPages,
    nextChannelFilter,
    sortInsightsPhotos,
    todayRange
} from './insights-admin-format';
import type {
    InsightsHourlyRate,
    InsightsPhoto,
    InsightsRate,
    InsightsReferrer,
    InsightsReferrerPath,
    InsightsReferrerReport,
    InsightsReport,
    InsightsSource,
    InsightsSortDirection,
    InsightsSortKey
} from './insights-admin-format';
import { insightsAdminStrings as strings } from './insights-admin-strings';

const statusSurface = requiredElement<HTMLElement>('status');
const tableHead = requiredElement<HTMLTableSectionElement>('insights-head');
const tableBody = requiredElement<HTMLTableSectionElement>('insights-body');
const emptyState = requiredElement<HTMLElement>('insights-empty');
const freshness = requiredElement<HTMLElement>('insights-freshness');
const legend = requiredElement<HTMLElement>('insights-legend');
const rangeButtons = [...document.querySelectorAll<HTMLButtonElement>('[data-range]')];
const channelSelect = requiredElement<HTMLSelectElement>('insights-channel');
const sourcesHead = requiredElement<HTMLTableSectionElement>('sources-head');
const sourcesBody = requiredElement<HTMLTableSectionElement>('sources-body');
const sourcesEmpty = requiredElement<HTMLElement>('sources-empty');
const sourcesNote = requiredElement<HTMLElement>('sources-note');
const sourceColumns = ['channel', 'visitors', 'userOpened', 'orderClicks', 'checkoutStarts', 'purchases', 'scans'] as const;
const referrersPanel = requiredElement<HTMLElement>('referrers-panel');
const referrersHead = requiredElement<HTMLTableSectionElement>('referrers-head');
const referrersBody = requiredElement<HTMLTableSectionElement>('referrers-body');
const referrersEmpty = requiredElement<HTMLElement>('referrers-empty');
const referrersNote = requiredElement<HTMLElement>('referrers-note');
const referrersClose = requiredElement<HTMLButtonElement>('referrers-close');
const referrerColumns = ['host', 'visitors', 'userOpened', 'orderClicks', 'checkoutStarts', 'purchases'] as const;
const columns: readonly InsightsSortKey[] = [
    'title',
    'pull',
    'autoShown',
    'attention',
    'exposure',
    'tileOpenRate',
    'userOpened',
    'averageView',
    'zoomRate',
    'orderClicks',
    'checkoutStarts',
    'purchases'
];
let selectedRange = '30days';
// The channel the photos table is filtered to; empty for every channel.
let selectedChannel = '';
let sortKey: InsightsSortKey = 'pull';
let sortDirection: InsightsSortDirection = defaultSortDirection('pull');
let report: InsightsReport | null = null;
let loadVersion = 0;
// Whether the Other sites breakdown is open; it follows the selected range and is independent of the filter.
let referrersOpen = false;
let referrersVersion = 0;
// The sites whose pages are listed; kept while the panel stays open, so a new range keeps them expanded.
const expandedReferrerHosts = new Set<string>();

function requiredElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (element == null) throw new Error(`Required element #${id} was not found.`);
    return element as T;
}

function setStatus(message: string, kind: 'status' | 'error' = 'status'): void {
    statusSurface.textContent = message;
    statusSurface.dataset.kind = kind;
}

function createElement<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);
    if (className != null) element.className = className;
    if (text != null) element.textContent = text;
    return element;
}

const adminSession = createAdminSession({
    signedOutMessage: strings.signedOut,
    loginFailedMessage: strings.loginFailed,
    requestFailedMessage: strings.requestFailed,
    setStatus,
    onAuthenticated: loadReport,
    onSignedOut: () => {
        report = null;
        setReferrersOpen(false);
        tableBody.replaceChildren();
        sourcesBody.replaceChildren();
        emptyState.hidden = true;
        sourcesEmpty.hidden = true;
        freshness.textContent = '';
        sourcesNote.textContent = '';
    }
});

async function loadReport(): Promise<void> {
    const version = ++loadVersion;
    setStatus(strings.loading);
    const query = new URLSearchParams({ range: selectedRange });
    if (selectedChannel.length > 0) query.set('channel', selectedChannel);
    const response = await adminSession.request(`/api/admin/insights?${query}`);
    if (version !== loadVersion) return;
    if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
    const payload = await response.json() as InsightsReport;
    if (version !== loadVersion) return;
    report = payload;
    renderChannelSelect();
    renderTable();
    renderSources();
    freshness.textContent = describeFreshness(payload);
    sourcesNote.textContent = payload.range === 'all' ? strings.sourcesRetention(payload.rawRetentionDays) : '';
    setStatus('');
}

function describeFreshness(current: InsightsReport): string {
    const generated = new Date(current.generatedUtc).toLocaleString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
    let text: string;
    if (current.range === todayRange) {
        text = strings.todayFreshness(formatTimeZoneName(current.timeZone, new Date(current.generatedUtc)), generated);
        const exposureStart = formatTodayExposureStart(current);
        if (exposureStart != null) text += strings.todayExposure(exposureStart);
    } else {
        text = strings.freshness(generated);
    }
    if (current.range === 'all') {
        text += current.dailyTotalsThrough == null ? strings.noDailyTotals : strings.dailyTotals(current.dailyTotalsThrough);
    }
    if (current.channel != null) text += strings.filteredTo(channelName(current.channel));
    return text;
}

/** A channel's plain name, using its campaign label when the Sources table has one. */
function channelName(channel: string): string {
    const source = report?.sources.find(entry => entry.channel === channel);
    return formatChannelName(channel, source?.label ?? null);
}

function renderChannelSelect(): void {
    const options = buildChannelOptions(report?.sources ?? [], selectedChannel, strings.allSources);
    channelSelect.replaceChildren(...options.map(option => {
        const element = createElement('option', undefined, option.label);
        element.value = option.value;
        return element;
    }));
    channelSelect.value = selectedChannel;
}

function renderHead(): void {
    const row = createElement('tr');
    for (const key of columns) {
        const label = strings.columns[key];
        const header = createElement('th');
        header.scope = 'col';
        if (key === sortKey) header.setAttribute('aria-sort', sortDirection);
        const button = createElement('button', 'insights-sort', label);
        button.type = 'button';
        button.dataset.sort = key;
        button.title = strings.sortBy(label);
        header.append(button);
        row.append(header);
    }
    tableHead.replaceChildren(row);
}

function renderTable(): void {
    renderHead();
    const photos = report?.photos ?? [];
    emptyState.hidden = photos.length > 0;
    emptyState.textContent = photos.length > 0
        ? ''
        : report?.channel != null ? strings.emptyForChannel(channelName(report.channel)) : strings.empty;
    tableBody.replaceChildren(...sortInsightsPhotos(photos, sortKey, sortDirection).map(createPhotoRow));
}

function createPhotoRow(photo: InsightsPhoto): HTMLTableRowElement {
    const row = createElement('tr');
    const rankCell = createElement('td', 'insights-rank', photo.pullScore == null ? strings.notRanked : strings.pullRank(photo.pullRank));
    if (photo.pullScore == null) rankCell.title = strings.notEnoughData;
    row.append(
        createPhotoCell(photo),
        rankCell,
        createElement('td', undefined, photo.autoShown.toLocaleString('en-US')),
        createRateCell(photo.attention),
        createExposureCell(photo),
        createHourlyRateCell(photo.tileOpenRate),
        createCountCell(photo.userOpened, strings.uniqueOpeners(photo.uniqueOpeners)),
        createElement('td', undefined, formatViewDuration(photo.averageViewMs)),
        createRateCell(photo.zoomRate),
        createElement('td', undefined, photo.orderClicks.toLocaleString('en-US')),
        createElement('td', undefined, photo.checkoutStarts.toLocaleString('en-US')),
        createPurchasesCell(photo.purchases));
    return row;
}

function createPhotoCell(photo: InsightsPhoto): HTMLTableCellElement {
    const cell = createElement('td');
    const wrapper = createElement('div', 'insights-photo');
    if (photo.thumbnailUrl != null) {
        const image = createElement('img', 'order-thumbnail');
        image.src = photo.thumbnailUrl;
        image.alt = '';
        image.loading = 'lazy';
        wrapper.append(image);
    } else {
        wrapper.append(createElement('span', 'order-thumbnail missing', '?'));
    }
    wrapper.append(photo.title == null
        ? createElement('span', 'insights-title removed', photo.thumbnailUrl == null ? strings.removedTitle : strings.untitled)
        : createElement('span', 'insights-title', photo.title));
    cell.append(wrapper);
    return cell;
}

function createRateCell(rate: InsightsRate): HTMLTableCellElement {
    const cell = createElement('td', 'insights-rate');
    cell.append(
        rate.rate == null
            ? createElement('span', 'insights-muted', strings.notEnoughData)
            : createElement('strong', undefined, formatPercent(rate.rate)),
        createElement('small', undefined, formatRateCounts(rate)));
    return cell;
}

function createExposureCell(photo: InsightsPhoto): HTMLTableCellElement {
    const cell = createElement('td', 'insights-rate');
    cell.append(createElement('strong', undefined, formatExposure(photo.exposureSeconds)));
    if (photo.exposureSeconds > 0) cell.append(createElement('small', undefined, strings.uniqueExposed(photo.uniqueExposed)));
    return cell;
}

function createHourlyRateCell(rate: InsightsHourlyRate): HTMLTableCellElement {
    const cell = createElement('td', 'insights-rate');
    cell.append(
        rate.perHour == null
            ? createElement('span', 'insights-muted', strings.notEnoughData)
            : createElement('strong', undefined, formatPerHour(rate.perHour)),
        createElement('small', undefined, formatHourlyRateCounts(rate)));
    return cell;
}

function createCountCell(count: number, detail: string): HTMLTableCellElement {
    const cell = createElement('td', 'insights-rate');
    cell.append(createElement('strong', undefined, count.toLocaleString('en-US')));
    if (count > 0) cell.append(createElement('small', undefined, detail));
    return cell;
}

function createPurchasesCell(purchases: number, shopEnabled = report?.shopEnabled !== false): HTMLTableCellElement {
    if (!shopEnabled) {
        const cell = createElement('td', 'insights-muted', strings.shopOff);
        cell.title = strings.shopOffTitle;
        return cell;
    }
    return createElement('td', undefined, purchases.toLocaleString('en-US'));
}

function renderSources(): void {
    const headRow = createElement('tr');
    for (const key of sourceColumns) {
        const header = createElement('th', undefined, strings.sourceColumns[key]);
        header.scope = 'col';
        headRow.append(header);
    }
    sourcesHead.replaceChildren(headRow);
    const sources = report?.sources ?? [];
    sourcesEmpty.hidden = sources.length > 0;
    sourcesEmpty.textContent = sources.length > 0 ? '' : strings.sourcesEmpty;
    sourcesBody.replaceChildren(...sources.map(createSourceRow));
}

function createSourceRow(source: InsightsSource): HTMLTableRowElement {
    const row = createElement('tr');
    row.dataset.channel = source.channel;
    const name = formatChannelName(source.channel, source.label);
    const selected = source.channel === selectedChannel;
    if (selected) row.setAttribute('aria-current', 'true');
    const nameCell = createElement('th');
    nameCell.scope = 'row';
    const button = createElement('button', 'insights-source-button insights-source-name', name);
    button.type = 'button';
    button.title = selected ? strings.clearSourceFilter(name) : strings.filterBySource(name);
    button.setAttribute('aria-pressed', String(selected));
    if (hasReferrerBreakdown(source.channel)) {
        // A separate control, so opening the breakdown never changes the filter and clicking the row still does.
        const toggle = createElement('button', 'secondary insights-referrers-toggle', strings.referrersToggle);
        toggle.type = 'button';
        toggle.dataset.referrersToggle = '';
        toggle.title = strings.referrersToggleLabel;
        toggle.setAttribute('aria-label', strings.referrersToggleLabel);
        toggle.setAttribute('aria-controls', referrersPanel.id);
        toggle.setAttribute('aria-expanded', String(referrersOpen));
        const wrapper = createElement('div', 'insights-source-cell');
        wrapper.append(button, toggle);
        nameCell.append(wrapper);
    } else {
        nameCell.append(button);
    }
    row.append(
        nameCell,
        createElement('td', undefined, source.visitors.toLocaleString('en-US')),
        createElement('td', undefined, source.userOpened.toLocaleString('en-US')),
        createElement('td', undefined, source.orderClicks.toLocaleString('en-US')),
        createElement('td', undefined, source.checkoutStarts.toLocaleString('en-US')),
        createPurchasesCell(source.purchases),
        createElement('td', source.scans == null ? 'insights-muted' : undefined, source.scans == null ? '—' : source.scans.toLocaleString('en-US')));
    return row;
}

function findReferrersToggle(): HTMLButtonElement | null {
    return sourcesBody.querySelector<HTMLButtonElement>('button[data-referrers-toggle]');
}

function setReferrersOpen(open: boolean): void {
    referrersOpen = open;
    referrersPanel.hidden = !open;
    findReferrersToggle()?.setAttribute('aria-expanded', String(open));
    if (open) {
        void loadReferrers().catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error'));
    } else {
        // Drop any response still on its way, so a closed panel is never filled.
        referrersVersion++;
        expandedReferrerHosts.clear();
        referrersBody.replaceChildren();
        referrersEmpty.hidden = true;
        referrersNote.textContent = '';
    }
}

async function loadReferrers(): Promise<void> {
    const version = ++referrersVersion;
    referrersBody.replaceChildren();
    referrersEmpty.hidden = false;
    referrersEmpty.textContent = strings.referrersLoading;
    referrersNote.textContent = '';
    const response = await adminSession.request(`/api/admin/insights/referrers?${new URLSearchParams({ range: selectedRange })}`);
    if (version !== referrersVersion) return;
    if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
    const payload = await response.json() as InsightsReferrerReport;
    if (version !== referrersVersion) return;
    renderReferrers(payload);
}

function renderReferrers(current: InsightsReferrerReport): void {
    const headRow = createElement('tr');
    for (const key of referrerColumns) {
        const header = createElement('th', undefined, strings.referrerColumns[key]);
        header.scope = 'col';
        headRow.append(header);
    }
    referrersHead.replaceChildren(headRow);
    referrersEmpty.hidden = current.referrers.length > 0;
    referrersEmpty.textContent = current.referrers.length > 0 ? '' : strings.referrersEmpty;
    referrersBody.replaceChildren(...current.referrers.flatMap((referrer, index) => createReferrerRows(referrer, index, current.shopEnabled)));
    referrersNote.textContent = current.range === 'all' ? strings.referrersRetention(current.rawRetentionDays) : '';
}

/** A site's row, followed by a row for each of its pages, hidden until its Pages button opens them. */
function createReferrerRows(referrer: InsightsReferrer, index: number, shopEnabled: boolean): HTMLTableRowElement[] {
    const row = createElement('tr');
    const hostCell = createElement('th');
    hostCell.scope = 'row';
    // Referrer hosts and paths come from visitors' browsers, so they are only ever shown as text, never as links.
    const host = createElement(
        'span',
        referrer.host == null ? 'insights-referrer-host missing' : 'insights-referrer-host',
        formatReferrerHost(referrer.host, strings.referrerNotRecorded));
    if (referrer.host == null) host.title = strings.referrerNotRecordedTitle;
    const pageRows: HTMLTableRowElement[] = [];
    if (referrer.host != null && hasReferrerPages(referrer)) {
        const expanded = expandedReferrerHosts.has(referrer.host);
        const pageIds = referrer.paths.map((_, pageIndex) => `referrer-${index}-page-${pageIndex}`);
        const toggle = createElement('button', 'secondary insights-referrers-toggle', strings.referrerPagesToggle);
        toggle.type = 'button';
        toggle.dataset.referrerPages = referrer.host;
        toggle.title = strings.referrerPagesToggleLabel(referrer.host);
        toggle.setAttribute('aria-label', strings.referrerPagesToggleLabel(referrer.host));
        toggle.setAttribute('aria-controls', pageIds.join(' '));
        toggle.setAttribute('aria-expanded', String(expanded));
        const wrapper = createElement('div', 'insights-source-cell');
        wrapper.append(host, toggle);
        hostCell.append(wrapper);
        pageRows.push(...referrer.paths.map((page, pageIndex) => createReferrerPageRow(referrer.host!, page, pageIds[pageIndex], expanded)));
    } else {
        hostCell.append(host);
    }
    row.append(
        hostCell,
        createElement('td', undefined, referrer.visitors.toLocaleString('en-US')),
        createElement('td', undefined, referrer.userOpened.toLocaleString('en-US')),
        createElement('td', undefined, referrer.orderClicks.toLocaleString('en-US')),
        createElement('td', undefined, referrer.checkoutStarts.toLocaleString('en-US')),
        createPurchasesCell(referrer.purchases, shopEnabled));
    return [row, ...pageRows];
}

function createReferrerPageRow(host: string, page: InsightsReferrerPath, id: string, visible: boolean): HTMLTableRowElement {
    const row = createElement('tr', 'insights-referrer-page');
    row.id = id;
    row.hidden = !visible;
    const pathCell = createElement('th');
    pathCell.scope = 'row';
    const path = createElement('span', 'insights-referrer-path');
    path.append(createElement('span', 'visually-hidden', strings.referrerPageOf(host)), formatReferrerPath(page.path));
    pathCell.append(path);
    row.append(
        pathCell,
        createElement('td', undefined, page.visitors.toLocaleString('en-US')),
        createElement('td', undefined, page.userOpened.toLocaleString('en-US')),
        createElement('td', undefined, page.orderClicks.toLocaleString('en-US')),
        createSiteOnlyCell(),
        createSiteOnlyCell());
    return row;
}

function createSiteOnlyCell(): HTMLTableCellElement {
    const cell = createElement('td', 'insights-muted', strings.referrerPageSiteOnly);
    cell.title = strings.referrerPageSiteOnlyTitle;
    return cell;
}

function toggleReferrerPages(toggle: HTMLButtonElement): void {
    const host = toggle.dataset.referrerPages;
    if (host == null) return;
    const expanded = toggle.getAttribute('aria-expanded') !== 'true';
    if (expanded) expandedReferrerHosts.add(host);
    else expandedReferrerHosts.delete(host);
    toggle.setAttribute('aria-expanded', String(expanded));
    for (const id of (toggle.getAttribute('aria-controls') ?? '').split(' ')) {
        const pageRow = id.length > 0 ? document.getElementById(id) : null;
        if (pageRow != null) pageRow.hidden = !expanded;
    }
}

function applyChannel(channel: string): void {
    if (channel === selectedChannel) return;
    selectedChannel = channel;
    void loadReport().catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error'));
}

function renderLegend(): void {
    legend.replaceChildren(...strings.legend.flatMap(([term, description]) => [
        createElement('dt', undefined, term),
        createElement('dd', undefined, description)
    ]));
}

tableHead.addEventListener('click', event => {
    const button = (event.target as Element | null)?.closest<HTMLButtonElement>('button[data-sort]');
    if (button == null) return;
    const key = button.dataset.sort as InsightsSortKey;
    if (key === sortKey) {
        sortDirection = sortDirection === 'ascending' ? 'descending' : 'ascending';
    } else {
        sortKey = key;
        sortDirection = defaultSortDirection(key);
    }
    renderTable();
    tableHead.querySelector<HTMLButtonElement>(`button[data-sort="${key}"]`)?.focus();
});

channelSelect.addEventListener('change', () => applyChannel(channelSelect.value));

sourcesBody.addEventListener('click', event => {
    if ((event.target as Element | null)?.closest('button[data-referrers-toggle]') != null) {
        setReferrersOpen(!referrersOpen);
        return;
    }
    const row = (event.target as Element | null)?.closest<HTMLTableRowElement>('tr[data-channel]');
    const channel = row?.dataset.channel;
    if (channel == null) return;
    applyChannel(nextChannelFilter(selectedChannel, channel));
});

referrersBody.addEventListener('click', event => {
    const toggle = (event.target as Element | null)?.closest<HTMLButtonElement>('button[data-referrer-pages]');
    if (toggle != null) toggleReferrerPages(toggle);
});

referrersClose.addEventListener('click', () => {
    setReferrersOpen(false);
    findReferrersToggle()?.focus();
});

for (const button of rangeButtons) {
    button.addEventListener('click', () => {
        const range = button.dataset.range;
        if (range == null || range === selectedRange) return;
        selectedRange = range;
        for (const other of rangeButtons) other.setAttribute('aria-pressed', String(other === button));
        void loadReport().catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error'));
        if (referrersOpen) {
            void loadReferrers().catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error'));
        }
    });
}

renderLegend();
renderHead();
void adminSession.initialize()
    .catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error'));
